// =============================================================================
// AGC: DROID FARM - Auto-Detect Review Component
// AGC: Compact design - shows one field at a time with prev/next navigation
// =============================================================================

import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Check, X, ChevronLeft, ChevronRight, Ruler, Save } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import type { AutoDetectResult } from './AutoDetectButton'

interface AutoDetectReviewProps {
  // AGC: Detection results to review
  results: AutoDetectResult
  // AGC: Callback when fields are confirmed
  onConfirm: (selectedFields: AutoDetectResult['features']) => void
  // AGC: Callback when review is cancelled
  onCancel: () => void
  // AGC: Callback when a field is selected/deselected (for map highlighting)
  onFieldSelect?: (fieldId: string, selected: boolean) => void
  // AGC: Currently hovered field (for map highlighting)
  onFieldHover?: (fieldId: string | null) => void
}

export const AutoDetectReview: React.FC<AutoDetectReviewProps> = ({
  results,
  onConfirm,
  onCancel,
  onFieldSelect,
  onFieldHover,
}) => {
  const { t } = useTranslation()
  
  // AGC: Track selected fields (all selected by default)
  const [selectedFields, setSelectedFields] = useState<Set<string>>(() => 
    new Set(results.features.map(f => f.id))
  )
  
  // AGC: Current field index for navigation
  const [currentIndex, setCurrentIndex] = useState(0)
  
  // AGC: Current field
  const currentField = results.features[currentIndex]
  const totalFields = results.features.length

  // AGC: Notify parent of current field for map highlighting
  useEffect(() => {
    if (currentField) {
      onFieldHover?.(currentField.id)
    }
    return () => onFieldHover?.(null)
  }, [currentField, onFieldHover])

  // AGC: Navigate to previous field
  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : totalFields - 1))
  }, [totalFields])

  // AGC: Navigate to next field
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev < totalFields - 1 ? prev + 1 : 0))
  }, [totalFields])

  // AGC: Toggle current field selection
  const toggleCurrentField = useCallback(() => {
    if (!currentField) return
    setSelectedFields(prev => {
      const next = new Set(prev)
      if (next.has(currentField.id)) {
        next.delete(currentField.id)
        onFieldSelect?.(currentField.id, false)
      } else {
        next.add(currentField.id)
        onFieldSelect?.(currentField.id, true)
      }
      return next
    })
  }, [currentField, onFieldSelect])

  // AGC: Handle confirm
  const handleConfirm = useCallback(() => {
    const selected = results.features.filter(f => selectedFields.has(f.id))
    onConfirm(selected)
  }, [results.features, selectedFields, onConfirm])

  // AGC: Calculate totals
  const selectedCount = selectedFields.size
  const totalArea = useMemo(() => {
    return results.features
      .filter(f => selectedFields.has(f.id))
      .reduce((sum, f) => sum + f.properties.areaHa, 0)
  }, [results.features, selectedFields])

  // AGC: Get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'bg-green-600'
    if (confidence >= 60) return 'bg-yellow-600'
    return 'bg-red-600'
  }

  if (!currentField) {
    return (
      <div className="bg-gray-900 rounded-t-2xl p-4 text-center text-gray-400">
        Nenhum campo detectado
      </div>
    )
  }

  const isSelected = selectedFields.has(currentField.id)
  const confidence = currentField.properties.confidence

  return (
    <div className="bg-gray-900 rounded-t-2xl shadow-2xl border-t border-gray-700">
      {/* AGC: Compact header with navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        {/* AGC: Previous button */}
        <button
          onClick={goToPrev}
          className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
          disabled={totalFields <= 1}
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        {/* AGC: Field counter and info */}
        <div className="text-center">
          <div className="text-white font-bold text-lg">
            Campo {currentIndex + 1} / {totalFields}
          </div>
          <div className="flex items-center justify-center gap-3 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Ruler className="w-3 h-3" />
              {currentField.properties.areaHa.toFixed(2)} ha
            </span>
            <span className={`px-2 py-0.5 rounded text-xs text-white ${getConfidenceColor(confidence)}`}>
              {confidence.toFixed(0)}%
            </span>
          </div>
        </div>

        {/* AGC: Next button */}
        <button
          onClick={goToNext}
          className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
          disabled={totalFields <= 1}
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* AGC: Selection toggle + actions */}
      <div className="flex items-center justify-between px-4 py-3">
        {/* AGC: Select/Deselect current field */}
        <button
          onClick={toggleCurrentField}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            isSelected 
              ? 'bg-green-600 text-white' 
              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
        >
          {isSelected ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
          {isSelected ? 'Selecionado' : 'Não selecionado'}
        </button>

        {/* AGC: Summary */}
        <div className="text-sm text-gray-400 text-right">
          <div>{selectedCount} campos selecionados</div>
          <div className="text-white font-medium">{totalArea.toFixed(2)} ha total</div>
        </div>
      </div>

      {/* AGC: Footer actions - compact */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-800">
        <button
          onClick={onCancel}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          <X className="w-4 h-4" />
          Cancelar
        </button>

        <button
          onClick={handleConfirm}
          disabled={selectedCount === 0}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
            selectedCount > 0
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Save className="w-4 h-4" />
          Salvar ({selectedCount})
        </button>
      </div>
    </div>
  )
}

export default AutoDetectReview
